import { useState } from 'react';
import { AxiosResponse } from 'axios';
import { Button, Dropdown, Popover, Whisper } from 'rsuite';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import { ResponseSegmentItems, SortKey } from '../../types';
import { formatSegmentTableDate, formatDetailChain, capitalizeFirstLetter } from '../../utils';
import styles from './SegmentsTable.module.scss';

type SegmentsTableProps = {
  data: AxiosResponse;
  onClickEdit: (id: number) => void;
  onClickMore: (id: number) => void;
  onSort: (id: string, direction: 'ASC' | 'DESC') => void;
};

type MenuPopoverProps = {
  onSelect?: (eventKey: any) => void;
  [key: string]: any;
};

const MenuPopover = ({ onSelect, ...rest }: MenuPopoverProps) => (
  <Popover {...rest} full>
    <Dropdown.Menu onSelect={onSelect}>
      <Dropdown.Item eventKey={1}>Excluir</Dropdown.Item>
    </Dropdown.Menu>
  </Popover>
);

const UP_ICON = <ArrowUpwardIcon className={styles['icon-sortable']} />;
const DOWN_ICON = <ArrowDownwardIcon className={styles['icon-sortable']} />;
const UNFOLD_ICON = <UnfoldMoreIcon className={styles['icon-sortable']} />;

export const SegmentsTable = ({ data, onClickEdit, onClickMore, onSort }: SegmentsTableProps) => {
  const [sortKey, setSortKey] = useState<SortKey>('NOME_SEGMENTO');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const dataTable = data?.data?.content;

  const handleSort = (key: SortKey) => {
    let newOrder: 'asc' | 'desc' = 'asc';

    if (sortKey === key) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }

    setSortKey(key);
    setSortOrder(newOrder);
    onSort(key, newOrder === 'asc' ? 'ASC' : 'DESC');
  };

  const getSortIcon = (key: SortKey) => {
    return sortKey === key ? (sortOrder === 'asc' ? UP_ICON : DOWN_ICON) : UNFOLD_ICON;
  };

  const handleSelect = (eventKey: number, id: number) => {
    if (eventKey === 1) {
      onClickMore(id);
    }
  };

  return (
    <div className={styles['table-container']}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th onClick={() => handleSort('NOME_SEGMENTO')}>
              Nome do segmento
              <span className={styles.sort}>{getSortIcon('NOME_SEGMENTO')}</span>
            </th>
            <th onClick={() => handleSort('TIPO_SEGMENTO')}>
              Tipo do segmento
              <span className={styles.sort}>{getSortIcon('TIPO_SEGMENTO')}</span>
            </th>
            <th>Detalhamento</th>
            <th onClick={() => handleSort('CRIADO_EM')}>
              Data da adição
              <span className={styles.sort}>{getSortIcon('CRIADO_EM')}</span>
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {dataTable?.length ? (
            dataTable?.map((item: ResponseSegmentItems) => (
              <tr key={item.segmento.id}>
                <td>{item.segmento.nomeSegmento}</td>
                <td>{capitalizeFirstLetter(item.segmento.tipoSegmento)}</td>
                <td className={styles.details}>
                  <div className={styles['chains-wrapper']}>
                    <span className={styles['chains-number']}>{item.itens.length}</span>
                    <span className={styles['chains-text']}> redes selecionadas</span>
                  </div>
                  <p>{formatDetailChain(item.itens)}</p>
                </td>
                <td>
                  <div>{formatSegmentTableDate(item.segmento.criadoEm)}</div>
                  <div className={styles.email}>{item.segmento.criadoPor}</div>
                </td>
                <td>
                  <div>
                    <Button appearance="link" className={styles['view-details-button']} onClick={() => onClickEdit(item?.segmento?.id)}>
                      Editar
                    </Button>
                  </div>
                  <div>
                    <Whisper placement="bottomEnd" trigger="click" speaker={<MenuPopover onSelect={key => handleSelect(key, item?.segmento?.id)} />}>
                      <Button appearance="link" className={styles['view-details-button']}>
                        Mais...
                      </Button>
                    </Whisper>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5}>
                <div className={styles['no-data']}>
                  <p className={styles['bold-text']}>Você não possui segmentos criados no momento.</p>
                  <p className={styles['regular-text']}>Para criar um, clique no botão acima.</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
